import {
  api_comment_likers_path,
  api_comments_path,
  api_create_comment_path,
  api_delete_comment_path,
  api_like_comment_path,
  api_unlike_comment_path,
  api_update_comment_path,
  report_community_comment_path,
} from '@routes';

import { ForumPost } from './forumPosts';
import request from './request';

export function getCommentsKey(commentableId: number, commentableType: CommentableType) {
  return ['COMMENTS_KEY', commentableId, commentableType];
}

export const COMMENTS_PER_PAGE = 50;

export enum CommentableType {
  ForumPost = 'ForumPost',
  Comment = 'Community::Comment',
  Article = 'Article',
  Briefing = 'Briefing',
  Graphic = 'Graphic',
}

export type CommentUser = {
  canInteractWithProfile: boolean;
  followStatus: boolean;
  identityId: number;
  id: number;
  guid: string;
  hasMessageAccess: boolean;
  name: string;
  picture_url: string;
  title_at_company: string;
  company_name: string;
  username: string;
  staff: boolean;
  profile_enabled: boolean;
};

export type CommentState = 'visible' | 'hidden' | 'deleted';

export type Comment = {
  id: number;
  created_at: string;
  body: string;
  snippet: string;
  likes_count: number;
  current_user_likes: unknown;
  promoted: boolean;
  forum_post: ForumPost;
  commentable_type: CommentableType;
  commentable_id: number;
  child_comments: Comment[];
  user: CommentUser;
  edited_at?: string;
  is_anonymous?: boolean;
  state: CommentState;
};

export interface CommentLikers {
  users?: CommentUser[];
  likers_for?: string;
}

export interface CommentsResponse {
  comments: Comment[];
  next_page: number;
  total_comments: number;
}

export type NewComment = Pick<
  Comment,
  'body' | 'commentable_type' | 'commentable_id' | 'is_anonymous'
>;
export type UpdateComment = Pick<Comment, 'id' | 'body' | 'commentable_type' | 'commentable_id'>;

// TODO: Add return types for all functions

export async function createComment(values: NewComment): Promise<Comment> {
  const resp = await request('post', api_create_comment_path(values));
  return resp;
}

export async function updateComment({
  id,
  body,
  commentable_id,
  commentable_type,
}: UpdateComment): Promise<Comment> {
  const resp = await request('post', api_update_comment_path(id), {
    body,
    commentable_id,
    commentable_type,
  });
  return resp;
}

export async function deleteComment(id: number): Promise<Record<string, never>> {
  const resp = await request('delete', api_delete_comment_path(id));
  return resp;
}

export async function getComments(params: {
  commentable_id: number;
  commentable_type: CommentableType;
  comment_id?: string;
  per_page?: number;
  page?: number;
  replies_per_comment?: number;
}): Promise<CommentsResponse> {
  const resp = await request('get', api_comments_path(params));
  return resp;
}

export async function getCommentLikers(id: number, renderMode: string): Promise<CommentLikers> {
  const resp = await request('get', api_comment_likers_path(), {
    comment_id: id,
    render_mode: renderMode,
  });
  return resp;
}

export async function reportComment(id: number): Promise<Record<string, never>> {
  const resp = await request('post', report_community_comment_path(id));
  return resp;
}

export async function likeComment(id: number): Promise<Record<string, never>> {
  const resp = await request('post', api_like_comment_path(id));
  return resp;
}

export async function unLikeComment(id: number): Promise<Record<string, never>> {
  const resp = await request('post', api_unlike_comment_path(id));
  return resp;
}
